import React, { useEffect, useState } from "react";
import { Box, Grid, Typography, useMediaQuery } from "@mui/material";
import axios from "axios";
import { browserVersions, hostURL } from "../../constants/constants";
import CaseFilters from "../CaseFilters/CaseFilters";
import CaseCard from "../CaseCard/CaseCard";
import DicomImage from "../DicomImage/DicomImage";

const extractValues = (customFields, fieldName) => {
  return customFields
    .filter((field) => field.name === fieldName)
    .flatMap((field) => field.value);
};

const CaseManagement = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTitle, setSelectedTitle] = useState("");
  const [cases, setCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const [userInteracted, setUserInteracted] = useState(false);
  const isPhone = useMediaQuery("(min-width :320px) and (max-width: 767px)");

  const handleFilter = (filtered) => {
    setFilteredCases(filtered);
  };

  const handleClickOpen = (title) => {
    setSelectedTitle(title);
    setDialogOpen(true);
  };

  const handleClose = () => {
    window.digitalData = window.digitalData || {};
    window.digitalData.link = {
      linkName: "Close",
      linkType: "Dicom Viewer",
      linkTitle: selectedTitle,
    };

    if (window._satellite) {
      window._satellite.track && window._satellite.track("link_click");
    }

    setDialogOpen(false);
    setSelectedImage(null);
  };

  const getAllCases = async () => {
    const getAllCasesURL = `${hostURL}/web/case`;

    await axios
      .get(getAllCasesURL, {
        headers: {
          app: "web",
        },
      })
      .then((res) => {
        const cases = res.data.cases;
        const urlParams = new URLSearchParams(window.location.search);
        const caseId = urlParams.get("caseId");

        if (caseId) {
          const caseIndex = cases.findIndex(
            (c) => c.caseId === parseInt(caseId, 10)
          );

          if (caseIndex !== -1) {
            const [selectedCase] = cases.splice(caseIndex, 1);
            cases.unshift(selectedCase);
          }
        }
        setCases(res.data.cases);
        setFilteredCases(res.data.cases);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    getAllCases();
  }, []);

  useEffect(() => {
    const filtered = cases.filter((item) => {
      const titleMatch = item.title
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      const labelsMatch = item.labels.some((label) =>
        label.toLowerCase().includes(searchQuery.toLowerCase())
      );
      const specialityMatch = item.customFields.some(
        (field) =>
          field.name === "speciality" &&
          field.value.some((val) =>
            val.toLowerCase().includes(searchQuery.toLowerCase())
          )
      );
      const modalityMatch = item.customFields.some(
        (field) =>
          field.name === "modality" &&
          field.value.some((val) =>
            val.toLowerCase().includes(searchQuery.toLowerCase())
          )
      );
      const bodypartMatch = item.customFields.some(
        (field) =>
          field.name === "bodypart" &&
          field.value.some((val) =>
            val.toLowerCase().includes(searchQuery.toLowerCase())
          )
      );

      return (
        titleMatch ||
        labelsMatch ||
        specialityMatch ||
        modalityMatch ||
        bodypartMatch
      );
    });
    handleFilter(filtered);
  }, [searchQuery, cases, userInteracted]);

  const getDicomImage = async (id) => {
    const getMedicaiURL = `${hostURL}/web/case/preview?caseId=${id}`;

    await axios
      .get(getMedicaiURL, {
        headers: {
          app: "web",
        },
      })
      .then((res) => {
        let imageUrl = res.data;
        setSelectedImage(imageUrl);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const showAlert = () => {
    const userAgent = navigator.userAgent;
    let browserName = "Unknown";
    let fullVersion = "Unknown";
    let versionNumber = 0;

    if (userAgent.indexOf("Chrome") > -1 && userAgent.indexOf("Edg") === -1) {
      browserName = "Chrome";
      fullVersion = userAgent.substring(userAgent.indexOf("Chrome") + 7);
      versionNumber = parseInt(fullVersion.split(" ")[0].split(".")[0]);
    } else if (userAgent.indexOf("Firefox") > -1) {
      browserName = "Firefox";
      fullVersion = userAgent.substring(userAgent.indexOf("Firefox") + 8);
      versionNumber = parseInt(fullVersion.split(" ")[0].split(".")[0]);
    } else if (userAgent.indexOf("Edg") > -1) {
      browserName = "Edge";
      fullVersion = userAgent.substring(userAgent.indexOf("Edg") + 4);
      versionNumber = parseInt(fullVersion.split(" ")[0].split(".")[0]);
    } else if (userAgent.indexOf("MSIE") > -1 || !!document.documentMode) {
      browserName = "Internet Explorer";
      fullVersion = userAgent.substring(userAgent.indexOf("MSIE") + 5);
      versionNumber = parseInt(fullVersion.split(" ")[0].split(".")[0]);
    } else if (
      userAgent.indexOf("Safari") > -1 &&
      userAgent.indexOf("Chrome") === -1
    ) {
      browserName = "Safari";
      fullVersion = userAgent.substring(userAgent.indexOf("Safari") + 7);
      if (userAgent.indexOf("Version") > -1) {
        fullVersion = userAgent.substring(userAgent.indexOf("Version") + 8);
      }
      versionNumber = parseInt(fullVersion.split(" ")[0].split(".")[0]);
    }

    if (userAgent.indexOf("CriOS") > -1) {
      browserName = "Chrome";
      fullVersion = userAgent.substring(userAgent.indexOf("CriOS") + 6);
      versionNumber = parseInt(fullVersion.split(" ")[0].split(".")[0]);
    } else if (userAgent.indexOf("EdgiOS") > -1) {
      browserName = "Edge";
      fullVersion = userAgent.substring(userAgent.indexOf("EdgiOS") + 7);
      versionNumber = parseInt(fullVersion.split(" ")[0].split(".")[0]);
    }

    let isSupported = true;

    if (browserName === "Chrome" && versionNumber < browserVersions.Chrome) {
      isSupported = false;
    } else if (browserName === "Edge" && versionNumber < browserVersions.Edge) {
      isSupported = false;
    } else if (
      browserName === "Safari" &&
      versionNumber < browserVersions.Safari
    ) {
      isSupported = false;
    } else if (
      browserName === "Samsung" &&
      versionNumber < browserVersions.Samsung
    ) {
      isSupported = false;
    } else if (
      browserName === "iOS Safari" &&
      versionNumber < browserVersions.iOS_Safari
    ) {
      isSupported = false;
    } else if (
      browserName === "Opera Mobile" &&
      versionNumber < browserVersions.OperaMobile
    ) {
      isSupported = false;
    } else if (
      browserName === "Firefox" &&
      versionNumber < browserVersions.Firefox
    ) {
      isSupported = false;
    }

    if (!isSupported) {
      alert(
        `DICOM viewer supports only ${browserName} versions greater than or equal to ${browserVersions[browserName]}. Please upgrade your current browser version. Thank you!`
      );
    }

    return isSupported;
  };

  const isIpadPro = useMediaQuery(
    "(min-width: 1024px) and (max-width: 1366px)"
  );

  const adobeAnalytics = (title) => {
    window.digitalData = window.digitalData || {};
    window.digitalData.link = {
      linkName: "View this case",
      linkType: "Dicom Viewer",
      linkTitle: title,
    };

    if (window._satellite) {
      window._satellite.track && window._satellite.track("link_click");
    }
  };

  useEffect(() => {
    const searchBox = document.getElementById("searchBox");

    const handleClick = () => {
      window.digitalData = window.digitalData || {};
      window.digitalData.link = {
        linkName: "Search clicked",
        linkType: "Dicom Viewer Search",
        linkTitle: "Search",
      };

      if (window._satellite) {
        window._satellite.track && window._satellite.track("link_click");
      }
    };

    if (searchBox) {
      searchBox.addEventListener("click", handleClick);
    }

    return () => {
      if (searchBox) {
        searchBox.removeEventListener("click", handleClick);
      }
    };
  }, []);

  return (
    <Grid
      container
      sx={{ padding: "10px", paddingTop: !isPhone && "45px" }}
      xs={12}
    >
      <CaseFilters
        searchQuery={searchQuery}
        filteredCases={filteredCases}
        setSearchQuery={setSearchQuery}
        setUserInteracted={setUserInteracted}
        cases={cases}
        onFilter={handleFilter}
        setCases={setCases}
        setFilteredCases={setFilteredCases}
      />
      {filteredCases.length === 0 ? (
        <Grid
          sx={{
            width: "100%",
          }}
        >
          <Typography
            variant="h6"
            align="center"
            color="textSecondary"
            sx={{ mt: 4 }}
          >
            No results found. Please edit or clear the applied filters.
          </Typography>
        </Grid>
      ) : (
        <div style={{ maxHeight: "auto", overflowY: "hidden" }}>
          <Box marginTop="20px">
            <Grid container spacing={2}>
              {filteredCases.map((card, index) => (
                <Grid
                  item
                  xs={12}
                  sm={isIpadPro ? 6 : 6}
                  md={isIpadPro ? 6 : 6}
                  lg={4}
                  key={card.caseId}
                  sx={{ gap: "30px" }}
                >
                  <CaseCard
                    key={index}
                    img={card.imageUrl}
                    title={card.title}
                    description={card.description}
                    tags={[
                      ...card.labels,
                      ...extractValues(card.customFields, "speciality"),
                      ...extractValues(card.customFields, "modality"),
                      ...extractValues(card.customFields, "bodypart"),
                    ]}
                    onClick={() => {
                      if (showAlert()) {
                        getDicomImage(card.caseId);
                        handleClickOpen(card.title);
                      }
                      adobeAnalytics(card.title);
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </div>
      )}
      <DicomImage
        open={dialogOpen}
        onClose={handleClose}
        imageUrl={selectedImage}
        title={selectedTitle}
      />
    </Grid>
  );
};

export default CaseManagement;
